import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const InventoryManagement = ({ onNavigate, userInfo }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [error, setError] = useState(null);

    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);

    const [formData, setFormData] = useState({
        item_name: '',
        category: 'vegetables',
        quantity: '',
        unit: 'kg',
        minimum_quantity: '',
        unit_price: '',
        supplier_name: '',
        expiry_date: '',
        is_perishable: 0
    });

    useEffect(() => {
        fetchInventoryItems();
    }, []);

    useEffect(() => {
        filterItems();
    }, [inventoryItems, selectedCategory, selectedStatus, searchQuery]);

    const fetchInventoryItems = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${API_URL}/mess/inventory`);
            const data = await response.json();
            if (data.success) {
                setInventoryItems(data.items || []);
            } else {
                setError(data.message || 'Failed to load inventory items');
            }
        } catch (err) {
            console.error('Error fetching inventory:', err);
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    const filterItems = () => {
        let filtered = [...inventoryItems];

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(item => item.category === selectedCategory);
        }

        if (selectedStatus !== 'all') {
            if (selectedStatus === 'low') {
                filtered = filtered.filter(item => item.quantity < item.minimum_quantity);
            } else if (selectedStatus === 'ok') {
                filtered = filtered.filter(item => item.quantity >= item.minimum_quantity);
            }
        }

        if (searchQuery) {
            filtered = filtered.filter(item =>
                (item.item_name && item.item_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.supplier_name && item.supplier_name.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        }

        setFilteredItems(filtered);
    };

    const handleAdd = async () => {
        if (!formData.item_name) {
            alert('Please enter item name');
            return;
        }
        try {
            const response = await fetch(`${API_URL}/mess/inventory`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            const data = await response.json();
            if (data.success) {
                fetchInventoryItems();
                setShowAddModal(false);
                resetForm();
            } else {
                alert(data.message || 'Failed to add item');
            }
        } catch (err) {
            console.error('Error adding item:', err);
            alert('Failed to add item');
        }
    };

    const handleEdit = async () => {
        if (!formData.item_name) {
            alert('Please enter item name');
            return;
        }
        try {
            const response = await fetch(`${API_URL}/mess/inventory/${currentItem.item_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            const data = await response.json();
            if (data.success) {
                fetchInventoryItems();
                setShowEditModal(false);
                resetForm();
            } else {
                alert(data.message || 'Failed to update item');
            }
        } catch (err) {
            console.error('Error updating item:', err);
            alert('Failed to update item');
        }
    };

    const handleDelete = async () => {
        try {
            const response = await fetch(`${API_URL}/mess/inventory/${currentItem.item_id}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
                fetchInventoryItems();
                setShowDeleteModal(false);
                setCurrentItem(null);
            } else {
                alert(data.message || 'Failed to delete item');
            }
        } catch (err) {
            console.error('Error deleting item:', err);
            alert('Failed to delete item');
        }
    };

    const openAddModal = () => {
        resetForm();
        setShowAddModal(true);
    };

    const openEditModal = (item) => {
        setCurrentItem(item);
        setFormData({
            item_name: item.item_name,
            category: item.category,
            quantity: item.quantity,
            unit: item.unit,
            minimum_quantity: item.minimum_quantity,
            unit_price: item.unit_price,
            supplier_name: item.supplier_name || '',
            expiry_date: item.expiry_date ? item.expiry_date.split('T')[0] : '',
            is_perishable: item.is_perishable
        });
        setShowEditModal(true);
    };

    const openDeleteModal = (item) => {
        setCurrentItem(item);
        setShowDeleteModal(true);
    };

    const resetForm = () => {
        setFormData({
            item_name: '',
            category: 'vegetables',
            quantity: '',
            unit: 'kg',
            minimum_quantity: '',
            unit_price: '',
            supplier_name: '',
            expiry_date: '',
            is_perishable: 0
        });
        setCurrentItem(null);
    };

    const handleLogout = () => {
        sessionStorage.clear();
        localStorage.clear();
        if (onNavigate) {
            onNavigate('hostel-login');
        }
    };

    const MenuItem = ({ emoji, label, active = false, route }) => (
        <div
            className={`menu-item ${active ? 'menu-item-active' : ''}`}
            onClick={() => {
                if (route && onNavigate) {
                    onNavigate(route);
                }
                setSidebarOpen(false);
            }}
        >
            <span className="menu-icon">{emoji}</span>
            <span className="menu-label">{label}</span>
            <div className="menu-glow"></div>
        </div>
    );

    const getStockStatus = (quantity, minimum) => {
        if (quantity < minimum) {
            return { status: 'low', color: '#f44336', icon: '⚠️' };
        } else if (quantity < minimum * 1.5) {
            return { status: 'medium', color: '#FF9800', icon: '⚡' };
        } else {
            return { status: 'ok', color: '#4CAF50', icon: '✓' };
        }
    };

    const getCategoryBadge = (category) => {
        const categoryEmojis = {
            'vegetables': '🥬',
            'fruits': '🍎',
            'grains': '🌾',
            'dairy': '🥛',
            'spices': '🧂',
            'oil': '🫗',
            'other': '📦'
        };
        return categoryEmojis[category] || '📦';
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#ffffff',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '6px solid #f3f3f3',
                    borderTop: '6px solid #52ab98',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{
                    color: '#52ab98',
                    fontSize: '18px',
                    fontWeight: '600'
                }}>Loading Inventory...</p>
                <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f7fa',
                padding: '2rem'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '3rem',
                    textAlign: 'center',
                    maxWidth: '500px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ fontSize: '64px', marginBottom: '1rem' }}>⚠️</div>
                    <h2 style={{ color: '#2b6777', marginBottom: '1rem' }}>Error Loading Inventory</h2>
                    <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
                    <button
                        onClick={fetchInventoryItems}
                        style={{
                            backgroundColor: '#52ab98',
                            color: 'white',
                            border: 'none',
                            padding: '12px 32px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .dashboard-container {
          display: flex;
          height: 100vh;
          background: linear-gradient(135deg, #f4f7fa 0%, #ffffff 100%);
          overflow: hidden;
        }

        .sidebar {
          width: 280px;
          background: linear-gradient(180deg, #2b6777 0%, #1e4d5a 100%);
          color: white;
          display: flex;
          flex-direction: column;
          position: fixed;
          left: 0;
          top: 0;
          height: 100vh;
          transform: translateX(-100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
        }

        .sidebar.open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 32px 24px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .sidebar-top {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 20px;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .user-avatar-sidebar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 20px;
        }

        .user-details-sidebar {
          display: flex;
          flex-direction: column;
        }

        .user-name-sidebar {
          font-weight: 600;
          font-size: 14px;
        }

        .user-role-sidebar {
          font-size: 12px;
          opacity: 0.8;
        }

        .sidebar-nav {
          flex: 1;
          padding: 24px 12px;
          overflow-y: auto;
        }

        .menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 8px;
          position: relative;
          overflow: hidden;
        }

        .menu-item:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .menu-item-active {
          background: rgba(255, 255, 255, 0.15);
          color: #52ab98;
        }

        .menu-icon {
          font-size: 20px;
        }

        .menu-label {
          font-size: 14px;
          font-weight: 500;
        }

        .menu-glow {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .logout-section {
          padding: 16px 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .logout-btn {
          width: 100%;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          padding: 12px 16px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .logout-btn:hover {
          background: rgba(255, 0, 0, 0.1);
          border-color: rgba(255, 0, 0, 0.3);
        }

        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }

        .overlay.show {
          opacity: 1;
          pointer-events: auto;
        }

        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .top-header {
          background: white;
          padding: 16px 32px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .menu-toggle {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          display: none;
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
        }

        .header-greeting {
          font-size: 12px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .header-name {
          font-size: 18px;
          font-weight: 600;
          color: #2b6777;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar-header {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #52ab98 0%, #2b6777 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .content-wrapper {
          flex: 1;
          overflow-y: auto;
          padding: 32px;
        }

        .content-area {
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
        }

        .hero-section {
          background: linear-gradient(135deg, #52ab98 0%, #2b6777 100%);
          color: white;
          padding: 40px 32px;
          border-radius: 16px;
          margin-bottom: 32px;
          box-shadow: 0 10px 30px rgba(82, 171, 152, 0.15);
        }

        .hero-content {
          max-width: 800px;
        }

        .hero-title {
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .hero-subtitle {
          font-size: 16px;
          opacity: 0.9;
        }

        .controls-section {
          background: white;
          padding: 24px;
          border-radius: 12px;
          margin-bottom: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .controls-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 20px;
        }

        .search-box {
          grid-column: 1 / -1;
        }

        .search-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .filter-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        .buttons-row {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .btn-add {
          background: #52ab98;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-add:hover {
          background: #3d8577;
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
        }

        .inventory-table {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .table-scroll {
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        thead {
          background: #f8f9fa;
          border-bottom: 2px solid #eee;
        }

        th {
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #2b6777;
          font-size: 14px;
        }

        td {
          padding: 16px;
          border-bottom: 1px solid #eee;
        }

        tbody tr:hover {
          background: #f8f9fa;
        }

        .item-name {
          font-weight: 600;
          color: #2b6777;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .stock-status {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          padding: 6px 12px;
          border-radius: 6px;
          background: #f0f0f0;
          width: fit-content;
        }

        .stock-ok {
          color: #4CAF50;
        }

        .stock-low {
          color: #f44336;
        }

        .stock-medium {
          color: #FF9800;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .btn-icon {
          background: none;
          border: 1px solid #ddd;
          padding: 6px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .btn-icon:hover {
          background: #f0f0f0;
          border-color: #999;
        }

        .btn-edit {
          background: #e3f2fd;
          color: #2196F3;
          border: none;
        }

        .btn-edit:hover {
          background: #bbdefb;
        }

        .btn-delete {
          background: #ffebee;
          color: #f44336;
          border: none;
        }

        .btn-delete:hover {
          background: #ffcdd2;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }

        .modal {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            transform: translateY(-50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .modal-header {
          padding: 24px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          font-size: 20px;
          font-weight: 600;
          color: #2b6777;
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #999;
        }

        .form-group {
          padding: 16px 24px;
          border-bottom: 1px solid #f0f0f0;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          padding: 16px 24px;
          border-bottom: 1px solid #f0f0f0;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .form-input,
        .form-select,
        .form-textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 3px rgba(82, 171, 152, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .checkbox-group {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 16px 24px;
        }

        .checkbox-group input {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }

        .modal-footer {
          padding: 16px 24px;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          border-top: 1px solid #eee;
        }

        .btn-secondary {
          background: #f0f0f0;
          color: #2b6777;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-secondary:hover {
          background: #e0e0e0;
        }

        .btn-primary {
          background: #52ab98;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          background: #3d8577;
        }

        .btn-danger {
          background: #f44336;
          color: white;
        }

        .btn-danger:hover {
          background: #da190b;
        }

        .delete-modal-content {
          padding: 40px 24px;
          text-align: center;
        }

        .delete-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .delete-text {
          font-size: 16px;
          color: #666;
          margin-bottom: 8px;
        }

        .delete-item-name {
          font-size: 18px;
          font-weight: 600;
          color: #2b6777;
          margin-bottom: 16px;
        }

        .delete-warning {
          background: #fff3cd;
          border-left: 4px solid #ffc107;
          padding: 12px;
          border-radius: 6px;
          color: #856404;
          font-size: 14px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }

        .empty-text {
          font-size: 18px;
          color: #999;
        }

        @media (min-width: 1024px) {
          .sidebar {
            transform: translateX(0);
            position: relative;
          }

          .close-btn {
            display: none;
          }

          .overlay {
            display: none;
          }

          .menu-toggle {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .sidebar {
            width: 100%;
            max-width: 320px;
          }

          .top-header {
            padding: 12px 16px;
          }

          .menu-toggle {
            display: block;
          }

          .content-wrapper {
            padding: 16px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          th, td {
            padding: 10px;
          }

          .action-buttons {
            flex-direction: column;
          }

          .hero-section {
            padding: 24px 20px;
          }

          .hero-title {
            font-size: 24px;
          }
        }

        @media (max-width: 480px) {
          .modal {
            max-width: 90vw;
          }

          .buttons-row {
            flex-direction: column;
          }

          .btn-add {
            width: 100%;
          }
        }
      `}</style>

            <div className="dashboard-container">
                <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-top">
                            <button onClick={() => setSidebarOpen(false)} className="close-btn">
                                ✕
                            </button>
                        </div>
                        <div className="user-info-sidebar">
                            <div className="user-avatar-sidebar">
                                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'M'}
                            </div>
                            <div className="user-details-sidebar">
                                <div className="user-name-sidebar">{userInfo?.name || 'Mess Manager'}</div>
                                <div className="user-role-sidebar">Mess Manager Portal</div>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <MenuItem emoji="🏠" label="Dashboard" route="hostel-mess-dashboard" />
                        <MenuItem emoji="🍽️" label="Food Management" route="hostel-mess-food" />
                        <MenuItem emoji="📊" label="Daily Report" route="hostel-mess-foodimage" />
                        <MenuItem emoji="📦" label="Inventory Management" active={true} route="hostel-mess-inventory-manage" />
                    </nav>

                    <div className="logout-section">
                        <button onClick={handleLogout} className="logout-btn">
                            🚪 Logout
                        </button>
                    </div>
                </div>

                <div className={`overlay ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)} />

                <div className="main-content">
                    <div className="top-header">
                        <div className="header-left">
                            <button onClick={() => setSidebarOpen(true)} className="menu-toggle">
                                ☰
                            </button>
                            <div className="header-title-section">
                                <div className="header-greeting">Welcome back,</div>
                                <div className="header-name">{userInfo?.name || 'Mess Manager'}</div>
                            </div>
                        </div>
                        <div className="header-right">
                            <div className="user-avatar-header">
                                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'M'}
                            </div>
                        </div>
                    </div>

                    <div className="content-wrapper">
                        <div className="content-area">
                            <div className="hero-section">
                                <div className="hero-content">
                                    <h1 className="hero-title">Inventory Management 📦</h1>
                                    <p className="hero-subtitle">Manage your hostel's food inventory and supplies</p>
                                </div>
                            </div>

                            <div className="controls-section">
                                <div className="controls-grid">
                                    <div className="search-box">
                                        <input
                                            type="text"
                                            className="search-input"
                                            placeholder="🔍 Search items or suppliers..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>

                                    <select
                                        className="filter-select"
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        <option value="all">All Categories</option>
                                        <option value="vegetables">🥬 Vegetables</option>
                                        <option value="fruits">🍎 Fruits</option>
                                        <option value="grains">🌾 Grains</option>
                                        <option value="dairy">🥛 Dairy</option>
                                        <option value="spices">🧂 Spices</option>
                                        <option value="oil">🫗 Oil</option>
                                        <option value="other">📦 Other</option>
                                    </select>

                                    <select
                                        className="filter-select"
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                    >
                                        <option value="all">All Stock Levels</option>
                                        <option value="ok">✓ Adequate Stock</option>
                                        <option value="low">⚠️ Low Stock</option>
                                    </select>
                                </div>

                                <div className="buttons-row">
                                    <button className="btn-add" onClick={openAddModal}>
                                        ➕ Add New Item
                                    </button>
                                </div>
                            </div>

                            {filteredItems.length > 0 ? (
                                <div className="inventory-table">
                                    <div className="table-scroll">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Item Name</th>
                                                    <th>Category</th>
                                                    <th>Current Stock</th>
                                                    <th>Unit</th>
                                                    <th>Min. Stock</th>
                                                    <th>Status</th>
                                                    <th>Supplier</th>
                                                    <th>Unit Price</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredItems.map((item) => {
                                                    const stockStatus = getStockStatus(item.quantity, item.minimum_quantity);
                                                    return (
                                                        <tr key={item.item_id}>
                                                            <td>
                                                                <div className="item-name">
                                                                    <span>{getCategoryBadge(item.category)}</span>
                                                                    <span>{item.item_name}</span>
                                                                </div>
                                                            </td>
                                                            <td style={{ textTransform: 'capitalize' }}>{item.category}</td>
                                                            <td>{item.quantity}</td>
                                                            <td>{item.unit}</td>
                                                            <td>{item.minimum_quantity}</td>
                                                            <td>
                                                                <div className={`stock-status stock-${stockStatus.status}`}>
                                                                    <span>{stockStatus.icon}</span>
                                                                    <span style={{ textTransform: 'capitalize' }}>
                                                                        {stockStatus.status === 'ok' ? 'OK' : stockStatus.status}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>{item.supplier_name || '-'}</td>
                                                            <td>₹{item.unit_price}</td>
                                                            <td>
                                                                <div className="action-buttons">
                                                                    <button
                                                                        className="btn-icon btn-edit"
                                                                        onClick={() => openEditModal(item)}
                                                                        title="Edit"
                                                                    >
                                                                        ✏️
                                                                    </button>
                                                                    <button
                                                                        className="btn-icon btn-delete"
                                                                        onClick={() => openDeleteModal(item)}
                                                                        title="Delete"
                                                                    >
                                                                        🗑️
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div className="inventory-table">
                                    <div className="empty-state">
                                        <div className="empty-icon">📦</div>
                                        <p className="empty-text">No inventory items found</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Add New Inventory Item</h2>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}>✕</button>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Item Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="e.g., Rice, Wheat Flour"
                                value={formData.item_name}
                                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Category *</label>
                                <select
                                    className="form-select"
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                >
                                    <option value="vegetables">Vegetables</option>
                                    <option value="fruits">Fruits</option>
                                    <option value="grains">Grains</option>
                                    <option value="dairy">Dairy</option>
                                    <option value="spices">Spices</option>
                                    <option value="oil">Oil</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Unit *</label>
                                <select
                                    className="form-select"
                                    value={formData.unit}
                                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                                >
                                    <option value="kg">kg</option>
                                    <option value="g">g</option>
                                    <option value="liters">liters</option>
                                    <option value="ml">ml</option>
                                    <option value="pieces">pieces</option>
                                    <option value="boxes">boxes</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Current Quantity *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="0"
                                    value={formData.quantity}
                                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Minimum Quantity *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="Alert threshold"
                                    value={formData.minimum_quantity}
                                    onChange={(e) => setFormData({ ...formData, minimum_quantity: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Unit Price (₹) *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="0"
                                    step="0.01"
                                    value={formData.unit_price}
                                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Supplier Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="e.g., Fresh Farms Ltd"
                                    value={formData.supplier_name}
                                    onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Expiry Date</label>
                            <input
                                type="date"
                                className="form-input"
                                value={formData.expiry_date}
                                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                            />
                        </div>

                        <div className="checkbox-group">
                            <input
                                type="checkbox"
                                id="perishable"
                                checked={formData.is_perishable === 1}
                                onChange={(e) => setFormData({ ...formData, is_perishable: e.target.checked ? 1 : 0 })}
                            />
                            <label htmlFor="perishable" style={{ margin: 0, cursor: 'pointer' }}>
                                This item is perishable
                            </label>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                            <button className="btn-primary" onClick={handleAdd}>➕ Add Item</button>
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Edit Inventory Item</h2>
                            <button className="modal-close" onClick={() => setShowEditModal(false)}>✕</button>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Item Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="e.g., Rice, Wheat Flour"
                                value={formData.item_name}
                                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Category *</label>
                                <select
                                    className="form-select"
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                >
                                    <option value="vegetables">Vegetables</option>
                                    <option value="fruits">Fruits</option>
                                    <option value="grains">Grains</option>
                                    <option value="dairy">Dairy</option>
                                    <option value="spices">Spices</option>
                                    <option value="oil">Oil</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Unit *</label>
                                <select
                                    className="form-select"
                                    value={formData.unit}
                                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                                >
                                    <option value="kg">kg</option>
                                    <option value="g">g</option>
                                    <option value="liters">liters</option>
                                    <option value="ml">ml</option>
                                    <option value="pieces">pieces</option>
                                    <option value="boxes">boxes</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Current Quantity *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="0"
                                    value={formData.quantity}
                                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Minimum Quantity *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="Alert threshold"
                                    value={formData.minimum_quantity}
                                    onChange={(e) => setFormData({ ...formData, minimum_quantity: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Unit Price (₹) *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="0"
                                    step="0.01"
                                    value={formData.unit_price}
                                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Supplier Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="e.g., Fresh Farms Ltd"
                                    value={formData.supplier_name}
                                    onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Expiry Date</label>
                            <input
                                type="date"
                                className="form-input"
                                value={formData.expiry_date}
                                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                            />
                        </div>

                        <div className="checkbox-group">
                            <input
                                type="checkbox"
                                id="perishable-edit"
                                checked={formData.is_perishable === 1}
                                onChange={(e) => setFormData({ ...formData, is_perishable: e.target.checked ? 1 : 0 })}
                            />
                            <label htmlFor="perishable-edit" style={{ margin: 0, cursor: 'pointer' }}>
                                This item is perishable
                            </label>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                            <button className="btn-primary" onClick={handleEdit}>💾 Save Changes</button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && currentItem && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h2 className="modal-title">Delete Item</h2>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>✕</button>
                        </div>

                        <div className="delete-modal-content">
                            <div className="delete-icon">🗑️</div>
                            <p className="delete-text">Are you sure you want to delete</p>
                            <p className="delete-item-name">{currentItem.item_name}?</p>
                            <div className="delete-warning">
                                ⚠️ This action cannot be undone. The inventory item will be permanently removed.
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-danger" onClick={handleDelete}>🗑️ Delete Item</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default InventoryManagement;